/**
 * Cookie utilities for secure authentication
 */

interface CookieOptions {
  expires?: Date
  path?: string
  domain?: string
  secure?: boolean
  sameSite?: 'strict' | 'lax' | 'none'
}

/**
 * Set a cookie with secure defaults
 */
export function setCookie(name: string, value: string, options: CookieOptions = {}) {
  const defaults: CookieOptions = {
    path: '/',
    secure: window.location.protocol === 'https:',
    sameSite: 'strict',
    ...options
  }

  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`

  if (defaults.expires) {
    cookieString += `; expires=${defaults.expires.toUTCString()}`
  }

  if (defaults.path) {
    cookieString += `; path=${defaults.path}`
  }

  if (defaults.domain) {
    cookieString += `; domain=${defaults.domain}`
  }

  if (defaults.secure) {
    cookieString += '; secure'
  }

  if (defaults.sameSite) {
    cookieString += `; samesite=${defaults.sameSite}`
  }

  document.cookie = cookieString
}

/**
 * Get a cookie value by name
 */
export function getCookie(name: string): string | null {
  const nameEQ = encodeURIComponent(name) + '='
  const cookies = document.cookie.split(';')

  for (let cookie of cookies) {
    cookie = cookie.trim()
    if (cookie.indexOf(nameEQ) === 0) {
      return decodeURIComponent(cookie.substring(nameEQ.length))
    }
  }

  return null
}

/**
 * Delete a cookie by name
 */
export function deleteCookie(name: string, options: Pick<CookieOptions, 'path' | 'domain'> = {}) {
  setCookie(name, '', {
    ...options,
    expires: new Date(0)
  })
}

/**
 * Check if a cookie exists
 */
export function hasCookie(name: string): boolean {
  return getCookie(name) !== null
}

/**
 * Parse JWT token to get expiration time
 */
export function getTokenExpiration(token: string): Date | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null

    const payload = JSON.parse(atob(parts[1]))
    if (!payload.exp) return null

    return new Date(payload.exp * 1000)
  } catch {
    return null
  }
}

/**
 * Check if a JWT token is expired
 */
export function isTokenExpired(token: string): boolean {
  const expiration = getTokenExpiration(token)
  if (!expiration) return true

  return expiration < new Date()
}

/**
 * Get time until token expiration in milliseconds
 */
export function getTimeUntilExpiration(token: string): number {
  const expiration = getTokenExpiration(token)
  if (!expiration) return 0

  const now = new Date()
  return Math.max(0, expiration.getTime() - now.getTime())
}

// Cookie names
export const COOKIE_NAMES = {
  AUTH_TOKEN: '4dgs_auth_token',
  CSRF_TOKEN: '4dgs_csrf_token'
} as const